/**
 * Pool Selection Utilities
 * 
 * Pure functions for managing pool selections without lodash cloneDeep.
 * These utilities use structural sharing for better performance.
 */

import { PoolSelections } from '../../services/selections/selections.model';
import {
  calculateLines,
  calculateSelectionsString,
  calculateSelectionsStringVotes,
  updateValidLegs
} from './parseSelections';

/**
 * Toggle a selection (bin) in a specific leg
 * Pure function - creates new object without mutation
 */
export function toggleSelectionInLeg(
  selections: PoolSelections,
  legId: number,
  bin: number
): PoolSelections {
  const legIdStr = String(legId);
  const currentLeg = selections.legs[legIdStr] || [];
  
  let newLegs: Record<string, number[]>;
  
  if (currentLeg.includes(bin)) {
    // Remove bin
    const filteredLeg = currentLeg.filter(b => b !== bin);
    if (filteredLeg.length === 0) {
      // Remove empty leg
      const remainingLegs: Record<string, number[]> = {};
      Object.keys(selections.legs).forEach(key => {
        if (key !== legIdStr) {
          remainingLegs[key] = selections.legs[key];
        }
      });
      newLegs = remainingLegs;
    } else {
      newLegs = {
        ...selections.legs,
        [legIdStr]: filteredLeg
      };
    }
  } else {
    // Add bin
    newLegs = {
      ...selections.legs,
      [legIdStr]: [...currentLeg, bin]
    };
  }
  
  // Parse selections to recalculate lines and strings
  return parseSelectionsFlow({
    ...selections,
    legs: newLegs
  });
}

/**
 * Remove all selections in a specific leg
 */
export function removeSelectionsInLeg(
  selections: PoolSelections,
  legId: number
): PoolSelections {
  const legIdStr = String(legId);
  const remainingLegs: Record<string, number[]> = {};
  Object.keys(selections.legs).forEach(key => {
    if (key !== legIdStr) {
      remainingLegs[key] = selections.legs[key];
    }
  });
  
  return {
    ...selections,
    legs: remainingLegs
  };
}

/**
 * Remove the last (first in array) selection in a specific leg
 */
export function removeLastSelectionInLeg(
  selections: PoolSelections,
  legId: number
): PoolSelections {
  const legIdStr = String(legId);
  const currentLeg = selections.legs[legIdStr];
  
  if (!currentLeg || currentLeg.length === 0) {
    return selections;
  }
  
  const [_first, ...remaining] = currentLeg;
  
  let newLegs: Record<string, number[]>;
  if (remaining.length === 0) {
    // Remove empty leg
    const remainingLegs: Record<string, number[]> = {};
    Object.keys(selections.legs).forEach(key => {
      if (key !== legIdStr) {
        remainingLegs[key] = selections.legs[key];
      }
    });
    newLegs = remainingLegs;
  } else {
    newLegs = {
      ...selections.legs,
      [legIdStr]: remaining
    };
  }
  
  return {
    ...selections,
    legs: newLegs
  };
}

/**
 * Flow function to parse selections
 * Combines multiple calculation steps
 */
function parseSelectionsFlow(selections: PoolSelections): PoolSelections {
  let result = selections;
  result = calculateLines(result);
  result = calculateSelectionsString(result);
  result = calculateSelectionsStringVotes(result);
  result = updateValidLegs(result);
  return result;
}

/**
 * Clear smartpick/cross_sell flags after manual selection changes
 */
export function clearAutoPickFlags(selections: PoolSelections): PoolSelections {
  const updates: Partial<PoolSelections> = {};
  
  if (selections.smartpick === 1) {
    updates.smartpick = 2;
  }
  
  if (selections.cross_sell === 1) {
    updates.cross_sell = 2;
  }
  
  return Object.keys(updates).length > 0
    ? { ...selections, ...updates }
    : selections;
}
